import { useEffect, useState } from 'react';
import axios from 'axios';
import { Globe, Check, Loader2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { API } from '@/App';
import { useLanguage } from '@/contexts/LanguageContext';

/**
 * Global language switcher (globe icon → dropdown).
 * Reads the offered languages from /api/i18n/languages and updates the
 * LanguageContext (which persists to localStorage + backend).
 */
export default function LanguageSwitcher({ compact = false }) {
  const { language, setLanguage, translating } = useLanguage();
  const [languages, setLanguages] = useState([]);

  useEffect(() => {
    axios.get(`${API}/i18n/languages`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then((r) => setLanguages(r.data.languages || []))
      .catch(() => setLanguages([{ code: 'en', name: 'English' }]));
  }, []);

  const current = languages.find((l) => l.code === language);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="language-switcher-btn"
          className="flex items-center gap-1.5 text-gray-300 hover:text-cyan-400 transition-colors"
          title="Change language"
          aria-label="Change language"
        >
          <Globe className="w-4 h-4" />
          {translating
            ? <Loader2 className="w-3 h-3 animate-spin" />
            : (!compact && <span className="text-xs font-medium uppercase">{language}</span>)}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-80 overflow-y-auto" data-no-translate>
        <DropdownMenuLabel>Language</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {languages.map((l) => (
          <DropdownMenuItem
            key={l.code}
            data-testid={`language-option-${l.code}`}
            onClick={() => setLanguage(l.code)}
            className="flex items-center justify-between cursor-pointer"
          >
            <span>{l.name}</span>
            {l.code === language && <Check className="w-4 h-4 text-cyan-500" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
